import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { cmsApiConfig } from '../../../cmsConfig';
import { BerattigadTillStodText } from '../../../types/Coach/ParticipationListItem';
import { ParticipationInterviewListItem } from '../../../types/ExternalAdvisor/ParticipationInterviewListItem';
import { InterestedInSupportText } from '../../../types/Enums/InterestedInSupport';
import { ParticipantToSignListItem } from '../../../types/ExternalAdvisor/ParticipantToSignListItem';
import {
  KartlaggningSignType,
  ParticipantDetails,
} from '../../../types/Coach/ParticipantDetails';
import { ExternalAdvisorPageTexts } from '../../../types/ExternalAdvisor/ExternalAdvisorTexts';
import { FinishParticipant } from '../../../types/ExternalAdvisor/FinishParticipant';
import { UpdateParticipantInformation } from '../../../types/ExternalAdvisor/UpdateParticipantInformation';
import { normalizeActivityDates } from '../../../Functions/DateTime';
import { AssessmentInterviewFormState } from '../../../pages/ExternalAdvisor/AssessmentInterview';
import { setUnauthorized } from '../../../redux/reducer/authSlice';
import { HttpStatusCode } from '../../../constants/HttpStatusCode';
import { PostBankruptcyAnswer } from '../../../types/ExternalAdvisor/PostBankrupctyAnswer';
import { PostAutomatedBankruptcyAnswer } from '../../../types/ExternalAdvisor/PostAutomatedBankrupctyAnswer';
export interface ReactConfig {
  applicationInsightsConnectionString: string;
}

const normalizeFinishParticipantModel = (
  model: FinishParticipant
): FinishParticipant => ({
  ...model,
  activity: normalizeActivityDates(model.activity),
});

const baseCoachQuery = fetchBaseQuery({
  baseUrl: cmsApiConfig.url + '/externalAdvisor',
  headers: {
    'X-CSRF': '1',
  },
  credentials: 'include',
});

const customBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseCoachQuery(args, api, extraOptions);

  if (result.error?.status === HttpStatusCode.Unauthorized) {
    api.dispatch(setUnauthorized());
  }

  return result;
};

export const externalAdvisorApi = createApi({
  reducerPath: 'externalAdvisorApi',
  baseQuery: customBaseQuery,
  tagTypes: [
    'ExternalAdvisorTexts',
    'NewParticipants',
    'OngoingParticipants',
    'FinishedParticipants',
    'ParticipantsToSign',
    'ParticipantDetails',
  ],
  endpoints: (builder) => ({
    getPageTexts: builder.query<ExternalAdvisorPageTexts, void>({
      query: () => ({
        url: `/getPageTexts`,
      }),
      providesTags: ['ExternalAdvisorTexts'],
    }),
    getNewParticipants: builder.query<ParticipationInterviewListItem[], void>({
      query: () => ({
        url: `/newParticipants`,
      }),
      providesTags: ['NewParticipants'],
      transformResponse: (response: ParticipationInterviewListItem[]) => {
        return response.map((item) => {
          return {
            ...item,
            berattigadTillStodText:
              BerattigadTillStodText[
                item.berattigadTillStod as keyof typeof BerattigadTillStodText
              ],
          } as ParticipationInterviewListItem;
        });
      },
    }),
    getOngoingParticipants: builder.query<
      ParticipationInterviewListItem[],
      void
    >({
      query: () => ({
        url: `/ongoingParticipants`,
      }),
      providesTags: ['OngoingParticipants'],
      transformResponse: (response: ParticipationInterviewListItem[]) => {
        return response.map((item) => {
          return {
            ...item,
            berattigadTillStodText:
              BerattigadTillStodText[
                item.berattigadTillStod as keyof typeof BerattigadTillStodText
              ],
          } as ParticipationInterviewListItem;
        });
      },
    }),
    getFinishedParticipants: builder.query<
      ParticipationInterviewListItem[],
      void
    >({
      query: () => ({
        url: `/finishedParticipants`,
      }),
      providesTags: ['FinishedParticipants'],
      transformResponse: (response: ParticipationInterviewListItem[]) => {
        return response.map((item) => {
          const participantFinishedDate =
            item.participantRejectedSupportDate != null
              ? item.participantRejectedSupportDate
                ? item.signDate != null
                  ? item.signDate
                  : null
                : item.participantFinishedDate != null
                  ? item.participantFinishedDate
                  : null
              : item.participantFinishedDate != null
                ? item.participantFinishedDate
                : null;
          return {
            ...item,
            interestedInSupportText:
              InterestedInSupportText[
                item.interestedInSupport as keyof typeof InterestedInSupportText
              ],
            participantFinishedDate: participantFinishedDate,
          } as ParticipationInterviewListItem;
        });
      },
    }),
    getParticipantsToSign: builder.query<ParticipantToSignListItem[], void>({
      query: () => ({
        url: `/participantsToSign`,
      }),
      providesTags: ['ParticipantsToSign'],
      transformResponse: (response: ParticipantToSignListItem[]) => {
        return response.map((item) => {
          return {
            ...item,
            interestedInSupportText:
              InterestedInSupportText[
                item.interestedInSupport as keyof typeof InterestedInSupportText
              ],
          } as ParticipantToSignListItem;
        });
      },
    }),
    getParticipantById: builder.query<ParticipantDetails, string>({
      query: (id) => ({
        url: `/participant?participantId=${id}`,
      }),
      transformResponse: (response: ParticipantDetails) => {
        return {
          ...response,
          berattigadTillStodText:
            response.berattigadTillStod !== undefined
              ? BerattigadTillStodText[
                  response.berattigadTillStod as keyof typeof BerattigadTillStodText
                ]
              : null,
        } as ParticipantDetails;
      },
      providesTags: (result, error, id) => {
        return [{ type: 'ParticipantDetails', id }];
      },
    }),
    updateParticipantInformation: builder.mutation<
      void,
      {
        id: string;
        body: UpdateParticipantInformation;
      }
    >({
      query: ({ id, body }) => ({
        url: `/updateParticipantInformation?participantId=${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => {
        return [{ type: 'ParticipantDetails', id }];
      },
    }),
    postKartlaggningSigning: builder.mutation<
      void,
      { id: string; signType: KartlaggningSignType }
    >({
      query: ({ id, signType }) => ({
        url: `/postKartlaggningSigning?participantId=${id}&signType=${signType}`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { id }) => {
        return [{ type: 'ParticipantDetails', id }];
      },
    }),
    finishParticipant: builder.mutation<
      void,
      { id: string; model: FinishParticipant }
    >({
      query: ({ id, model }) => ({
        url: `/finishParticipant?participantId=${id}`,
        method: 'POST',
        body: normalizeFinishParticipantModel(model),
      }),
      invalidatesTags: (result, error, { id }) => {
        return [
          { type: 'ParticipantDetails', id: id },
          'NewParticipants',
          'OngoingParticipants',
          'FinishedParticipants',
          'ParticipantsToSign',
        ];
      },
    }),
    postAssessmentInterview: builder.mutation<
      void,
      { id: string; body: AssessmentInterviewFormState }
    >({
      query: ({ id, body }) => ({
        url: `/assessmentInterview?participantId=${id}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) => {
        return [{ type: 'ParticipantDetails', id }];
      },
    }),
    postBankruptcy: builder.mutation<
      void,
      { id: string; body: PostBankruptcyAnswer }
    >({
      query: ({ id, body }) => ({
        url: `/postBankruptcy?participantId=${id}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) => {
        return [{ type: 'ParticipantDetails', id }];
      },
    }),
    postAutomatedBankruptcy: builder.mutation<
      void,
      { id: string; body: PostAutomatedBankruptcyAnswer }
    >({
      query: ({ id, body }) => ({
        url: `/postAutomatedBankruptcy?participantId=${id}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) => {
        return [{ type: 'ParticipantDetails', id }];
      },
    }),
  }),
});

export const {
  useLazyGetPageTextsQuery,
  useLazyGetNewParticipantsQuery,
  useLazyGetOngoingParticipantsQuery,
  useLazyGetFinishedParticipantsQuery,
  useLazyGetParticipantsToSignQuery,
  useGetParticipantByIdQuery,
  useUpdateParticipantInformationMutation,
  usePostKartlaggningSigningMutation,
  useFinishParticipantMutation,
  usePostAssessmentInterviewMutation,
  usePostBankruptcyMutation,
  usePostAutomatedBankruptcyMutation,
} = externalAdvisorApi;
